import Color from "color";

/**
 * Sleeps for the specified number of milliseconds, but only in non-production environments.
 *
 * @remarks
 * Useful for simulating loading states or testing Suspense fallbacks during development.
 *
 * @param ms - The number of milliseconds to sleep.
 * @example
 * ```ts
 * await devSleep();       // sleeps 1500ms in dev, no-op in production
 * await devSleep(500);    // sleeps 500ms in dev, no-op in production
 * ```
 */
export async function devSleep(ms: number = 1500): Promise<void> {
  if (process.env.NODE_ENV !== "production") {
    console.log(`slept for ${ms} ms`);
    await new Promise((res) => setTimeout(res, ms));
  }
}

export async function isDev(): Promise<boolean> {
  return process.env.NODE_ENV !== "production";
}

export function getISOWeek(date: Date): number {
  const tempDate = new Date(date.getTime());
  tempDate.setHours(0, 0, 0, 0);
  tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7));
  const week1 = new Date(tempDate.getFullYear(), 0, 4);
  return 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

export function titleCase(str: string) {
  return str
    .toLowerCase()
    .split(" ")
    .map(function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

export function getSimpleWeekOfYear(date: Date): number {
  const start = new Date(date.getFullYear(), 0, 1);
  const diff = date.getTime() - start.getTime();
  return Math.ceil((diff / 86400000 + start.getDay() + 1) / 7);
}

export function hexToRGBA(hex: string, alpha: number = 0.18): string {
  return Color(hex).alpha(alpha).rgb().string();
}

export function fadeHex(hex: string, fade: number = 0.5): string {
  const base = Color(hex);
  return `${base.darken(fade).rgb().hex()}`;
}

type DateParts = {
  year: number;
  month: number; // 1–12
  day: number;
  hour: number;
  minute: number;
  second: number;
};

// export function getDayOfWeek(day: number, abbreviated: boolean = true): string {
//   const fullNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
//   const shortNames = fullNames.map((name) => name.slice(0, 3));
//   if (day < 1 || day > 31) throw new Error("Invalid month number");
//   return abbreviated ? shortNames[day - 1] : fullNames[month - 1];
// }

export function pad(
  input: any,
  length: number = 2,
  padChar: string = "0",
  direction: "left" | "right" = "left",
): string {
  input = input.toString();
  if (direction === "left") while (input.length < length) input = padChar + input;
  else while (input.length < length) input = input + padChar;
  return input;
}

export function sameDay(d1: Date, d2: Date): boolean {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}

export function getMonthName(month: number, abbreviated: boolean = true): string {
  const fullNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const shortNames = fullNames.map((name) => name.slice(0, 3));
  if (month < 1 || month > 12) throw new Error("Invalid month number");
  return abbreviated ? shortNames[month - 1] : fullNames[month - 1];
}

export function getDateParts(date: Date = new Date(), timeZone?: string): DateParts {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);

  const getPart = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);

  return {
    year: getPart("year"),
    month: getPart("month"),
    day: getPart("day"),
    hour: getPart("hour"),
    minute: getPart("minute"),
    second: getPart("second"),
  };
}
export const saveImage = async (imageUrl: string) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const base64 = await new Promise((resolves) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolves(reader.result);
    });

    return base64;
  } catch (error) {
    throw Error(error);
  }
};

export function autoFormatDateInput(input: string): string {
  const digits = input.replace(/\D/g, "");

  if (digits.length < 4) return digits; // too short to autoformat

  let mm = digits.slice(0, 2);
  let dd = digits.slice(2, 4);
  let yyyy = digits.slice(4);

  if (yyyy.length === 2) {
    // handle 2-digit year
    const num = Number(yyyy);
    yyyy = num < 50 ? `20${yyyy}` : `19${yyyy}`;
  }

  return [mm, dd, yyyy].filter(Boolean).join("/");
}
